import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Building2, Shield, Eye, FileText, ArrowRight, Target, CheckCircle, Gavel } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(true);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const Fogedom = () => {
  const [heroRef, heroInView] = useInView();
  const [roleRef, roleInView] = useInView();
  const [garantiesRef, garantiesInView] = useInView();
  const [governanceRef, governanceInView] = useInView();

  const roles = [
    { icon: Eye, title: 'Instruction des demandes', desc: 'Le comité du fonds examine les dossiers, vérifie les critères et propose les plafonds et modalités d’intervention.', gradient: 'from-amber-500 to-orange-500' },
    { icon: Shield, title: 'Soutien aux projets', desc: 'Prévention, ingénierie et accompagnement des projets économiques, sociaux et territoriaux selon le règlement du fonds.', gradient: 'from-green-500 to-emerald-500' },
    { icon: Gavel, title: 'Gouvernance coopérative', desc: 'Les interventions sont décidées dans le cadre des statuts et du règlement spécifique du fonds.', gradient: 'from-blue-500 to-cyan-500' },
    { icon: FileText, title: 'Suivi des engagements', desc: 'Comptabilité analytique dédiée, traçabilité des décisions, suivi des engagements et rapport annuel.', gradient: 'from-purple-500 to-pink-500' },
  ];

  const garanties = [
    { title: 'BATICO — RCDS', subtitle: 'Responsabilité civile et décennale solidaire', desc: 'Accompagnement des professionnels du bâtiment face à des désordres relevant de la responsabilité décennale.', gradient: 'from-blue-500 to-cyan-500' },
    { title: 'NOMINATIVE — RCDS au chantier', subtitle: 'Chantier identifié', desc: 'Accompagnement civil et solidaire pour un chantier déterminé.', gradient: 'from-green-500 to-emerald-500' },
    { title: 'DOMIUM (DOS)', subtitle: 'Entraide après travaux', desc: 'Prévention et accompagnement des difficultés techniques ou civiles après travaux.', gradient: 'from-purple-500 to-pink-500' },
    { title: 'CAP BÂTI', subtitle: 'Construction de maisons individuelles', desc: 'Prévention et accompagnement des projets de construction de maisons individuelles.', gradient: 'from-amber-500 to-orange-500' },
    { title: 'CAP BÂTI+', subtitle: 'Difficultés de livraison', desc: 'Accompagnement solidaire en présence de difficultés de livraison ou de dépassement de prix.', gradient: 'from-red-500 to-rose-500' },
    { title: 'ARCHOS — RCDS', subtitle: 'Professions intellectuelles du bâtiment', desc: 'Soutien civil et solidaire pour l’ingénierie, la maîtrise d’œuvre et les missions techniques.', gradient: 'from-indigo-500 to-purple-500' },
  ];

  const governance = [
    { title: 'Conseil coopératif', desc: 'Gouvernance de la SCIC selon les statuts' },
    { title: 'Comité FOGEDOM-SCIC', desc: 'Instruction, vérification et propositions d’intervention' },
    { title: 'Règlement du fonds', desc: 'Critères, plafonds, modalités et gouvernance' },
    { title: 'Comptabilité analytique', desc: 'Suivi des ressources, dépenses et engagements' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=80" 
            alt="Board meeting and decision-making" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-amber-600/20 to-orange-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 border border-amber-500/30 mb-6 px-4 py-2">
              <Building2 className="w-4 h-4 mr-2" /> Fonds interne de la SCIC O’SCOP
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-amber-100 to-white bg-clip-text text-transparent">FOGEDOM-SCIC</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-4 leading-relaxed font-semibold">
              Soutien, prévention et ingénierie coopérative
            </p>
            
            <p className="text-lg text-slate-400 max-w-3xl mb-10 leading-relaxed">
              FOGEDOM-SCIC est un <span className="text-amber-400">fonds interne d’OBJECTIF SCOP OUTREMER</span>,
              constitué en section analytique sans personnalité juridique distincte. Il accompagne les projets
              selon les critères et les modalités de son règlement.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
                Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg transition-all duration-300" onClick={() => window.location.href = '/fogedom/accompagnement'}>
                Accompagnement
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Role Section */}
      <section ref={roleRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${roleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 mb-4 px-4 py-2"><Target className="w-4 h-4 mr-2" /> Notre rôle</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Le Fonds au service des <span className="text-amber-400">projets</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {roles.map((role, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${roleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${role.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <role.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{role.title}</h3>
                <p className="text-slate-400 leading-relaxed">{role.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Garanties Section */}
      <section ref={garantiesRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-green-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${garantiesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-green-500/20 text-green-300 border border-green-500/30 mb-4 px-4 py-2"><Shield className="w-4 h-4 mr-2" /> Dispositifs de l’écosystème</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Des parcours pour vos <span className="text-green-400">projets</span></h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">Chaque dispositif conserve ses conditions d’accès et d’intervention, présentées lors de l’étude de la demande.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {garanties.map((garantie, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 hover:border-green-500/50 transition-all duration-500 hover:-translate-y-2 ${garantiesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${garantie.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{garantie.title}</h3>
                <p className="text-sm text-green-400 mb-3">{garantie.subtitle}</p>
                <p className="text-sm text-slate-400">{garantie.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section ref={governanceRef} className="py-24 bg-gradient-to-br from-amber-600 via-orange-600 to-amber-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Gouvernance <span className="text-amber-200">coopérative</span></h2>
            <p className="text-white/80 max-w-3xl mx-auto">Organisation prévue aux articles 36 et 37 des statuts d’OBJECTIF SCOP OUTREMER adoptés le 10 août 2026.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {governance.map((item, index) => (
              <div key={index} className={`bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 text-center ${governanceInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-12 h-12 mx-auto rounded-xl bg-white/20 flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-white/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Besoin d'un <span className="text-amber-400">accompagnement</span> ?</h2>
          <p className="text-xl text-slate-400 mb-10">Découvrez comment nous pouvons vous accompagner dans vos projets.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/fogedom/accompagnement'}>
              Accompagnement <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-slate-600 text-white hover:bg-slate-800 px-10 py-6 text-lg transition-all duration-300" onClick={() => window.location.href = '/contact'}>
              Nous contacter
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Fogedom;
